import { useEffect, useState } from 'react';

/**
 * Update Notification Banner Props
 */
interface UpdateNotificationBannerProps {
  /** Callback when user clicks "Refresh Now" button */
  onRefresh: () => void;
}

/**
 * Update Notification Banner Component
 *
 * Displays a small, persistent banner at the top of the page when a new
 * version of the application is available. The banner is not dismissible
 * to ensure users stay on the most recent version.
 *
 * Features:
 * - Small height (~40px) - visible but not annoying
 * - Blue theme matching department colors
 * - Slide-down animation on appear
 * - Clear call-to-action button
 * - Not dismissible (ensures users update)
 *
 * @param {UpdateNotificationBannerProps} props - Component props
 *
 * @example
 * <UpdateNotificationBanner onRefresh={performUpdate} />
 */
export function UpdateNotificationBanner({ onRefresh }: UpdateNotificationBannerProps) {
  const [isVisible, setIsVisible] = useState(false);

  // Trigger slide-down animation after mount
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsVisible(true);
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  return (
    <div
      className={`
        fixed top-0 left-0 right-0 z-50
        bg-info text-ink-inverse shadow-lg
        transition-transform duration-300 ease-out
        ${isVisible ? 'translate-y-0' : '-translate-y-full'}
      `}
      role="alert"
      aria-live="polite"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="py-3 flex items-center justify-between">
          {/* Message */}
          <div className="flex items-center space-x-3">
            {/* Icon */}
            <svg
              className="h-5 w-5 flex-shrink-0"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>

            {/* Text */}
            <p className="text-sm font-medium">
              A new version is available. Please refresh to get the latest updates.
            </p>
          </div>

          {/* Refresh Button */}
          <button
            onClick={onRefresh}
            className="
              ml-4 px-4 py-1.5
              bg-surface text-info
              rounded-md font-medium text-sm
              hover:bg-info-subtle
              focus:outline-none focus:ring-2 focus:ring-ink-inverse focus:ring-offset-2 focus:ring-offset-info
              transition-colors duration-200
              flex-shrink-0
            "
            type="button"
          >
            Refresh Now
          </button>
        </div>
      </div>
    </div>
  );
}
